import * as _ from 'underscore';

import { IGlob } from '../../../pdc-platform/src/ts/Glob';

import { IValuesCollection } from './appTheme';
import ParamsValuesEdit from './ParamsValuesEdit';
import { IWidth, TitlePositionType } from './Interfaces';
import { IDataEditValues } from '../tsx/dataEdit/Interfaces';

export interface IDefaultWidgetParameters {
    name?: string;
    id?: number;
    configurationChangeDate?: string;
    theme?: IValuesCollection;
    role?: string;
    description?: string;
}

export interface IValidator {
    (value: any, glob?: IGlob, values?: IDataEditValues): string[] | undefined;
}

export interface IValidationResult {
    result?: boolean;
    errors?: string[];
}

export interface IDirectOptions {
    theme?: IValuesCollection;
    dataSourceID?: number;
    readOnly?: boolean;
}

export interface IParameterFieldBaseOptions {
    theme?: IValuesCollection;
    tabID?: number;
    mandatory?: boolean;
    setFocus?: boolean;
    readOnly?: boolean;
    allowNull?: boolean;
    hidden?: boolean | { (values: IDataEditValues): boolean };
    inlineHelp?: string;
    noTitle?: boolean;
    titlePosition?: TitlePositionType;
    width?: IWidth;
    minWidth?: IWidth;
    height?: string;

    fix?(value, validators, widgetData?): any;
    isEqual?(value1, value2): boolean;
    getDirectOptions?(value, values, theme?: IValuesCollection, widgetData?: ParamsValuesEdit): IDirectOptions;
    onChange?(values, value, fieldName: string): void;
}

export interface IParameterFieldBase<TOptions extends IParameterFieldBaseOptions> {
    glob: IGlob;
    title?: string;
    errors?: any[];
    brokenValidators?: IValidator[];
    defaultVal?: any;
    options?: TOptions;
    directOptions?;

    getDefault?(...args): any;
    isEqual?(value1: any, value2: any): boolean;
    clearValidationState?(): void;
}

export default class ParameterFieldBase<TOptions extends IParameterFieldBaseOptions>
    implements IParameterFieldBase<TOptions>
{
    public errors: any[] = [];
    public brokenValidators: IValidator[] = [];

    private validators: IValidator[];

    constructor(
        public glob: IGlob,
        public title: string,
        defaultValidators?: IValidator[],
        validators: IValidator[] = [],
        public defaultVal?: any,
        public options?: TOptions,
        public directOptions?
    ) {
        this.validators =
            validators != null ? (defaultValidators != null ? defaultValidators.concat(validators) : validators) : [];
    }

    public getDefault(...args) {
        const _args = Array.prototype.slice.call(arguments, 0);

        if (typeof this.defaultVal === 'function') {
            try {
                //return this.defaultVal(args);
                return this.defaultVal.apply(this, _args);
            } catch (e) {
                return;
            }
        }

        return this.defaultVal;
    }

    public onChange(values, fieldName: string, value): { [fieldName: string]: any } {
        if (this.options != null && typeof this.options.onChange === 'function') {
            values = this.options.onChange(values, value, fieldName);
        }

        return values;
    }

    public isEqual(value1, value2) {
        return value1 === value2;
    }

    public clearValidationState() {
        this.errors = [];
        this.brokenValidators = [];
    }

    public validate(value: any, values?: IDataEditValues, validatorsToIgnore: IValidator[] = []): IValidationResult {
        const res: IValidationResult = {};

        if (value == null && this.options && !this.options.mandatory) {
            res.result = true;
            this.errors = [];

            return res;
        }

        let brokenValidators = _.map(_.difference(this.validators, validatorsToIgnore), (validator) => {
            const errors = validator.call(this, value, this.glob, values);

            if (errors == null || (errors instanceof Array && errors.length === 0)) {
                return;
            }

            return {
                source: validator,
                errors: errors
            };
        });

        brokenValidators = _.compact(brokenValidators);

        let errors = Array.prototype.concat.apply([], _.pluck(brokenValidators, 'errors'));

        if (errors.length === 0) {
            // && !displayRequired
            res.result = true;
        } else {
            res.result = false;
            res.errors = errors;
        }

        this.errors = errors;
        this.brokenValidators = _.pluck(brokenValidators, 'source');
        return res;
    }

    public getDefaultOptions(): TOptions {
        return {} as TOptions;
    }

    public getDefaultDirectOptions(): IDirectOptions {
        return {};
    }

    public getOptions(): TOptions {
        return _.extend(this.getDefaultOptions(), this.options);
    }

    public getDirectOptions(value, values?, theme?: IValuesCollection, widgetData?: ParamsValuesEdit): IDirectOptions {
        const objs = [this.getDefaultDirectOptions(), this.directOptions];

        if (this.options != null && typeof this.options.getDirectOptions === 'function') {
            objs.push(this.options.getDirectOptions(value, values, theme, widgetData));
        }

        return _.extend.apply(_, objs);
    }

    public fix(value, values) {
        const options = this.getOptions();

        let fix;

        if (options.fix != null && !(options.fix instanceof Array)) {
            fix = [options.fix];
        } else {
            fix = options.fix;
        }

        if (fix != null) {
            value = _.reduce<any, any>(
                fix,
                (value, func) => {
                    return func(value, this.brokenValidators, values);
                },
                value
            );
        }

        return value;
    }

    private getDefaultEditViews(value): {
        [id: string]: {
            title: string;

            Component: any;
            componentOptions: any;
            componentProps: any;

            input: (value: any) => any;
            output: (input: any, value: any) => any;
        };
    } {
        var _constr: any = this.constructor;
        const directOptions = this.getDirectOptions(value);

        return {
            __main: {
                title: 'Main',

                Component: _constr.EditComponent,
                componentOptions: this.getOptions(),
                componentProps: directOptions,

                input: function (value) {
                    return value;
                },
                output: function (input, value) {
                    return value;
                }
            }
        };
    }

    public getEditViews(value): {
        [id: string]: {
            title: string;

            Component: any;
            componentOptions: any;
            componentProps: any;

            input: (value: any) => any;
            output: (input: any, value: any) => any;
        };
    } {
        return {};
    }

    public collectEditViews(value) {
        return _.extend({}, this.getDefaultEditViews(value), this.getEditViews(value));
    }
}
